import React, { useState, useContext, useMemo } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, btnSec, bdg, inpS, sp, rad } from "../../constants/styles";
import Fld from "../common/Fld";
import Ic from "../common/Ic";
import SignaturePad from "./SignaturePad";
import { defaultTerms, generateAgreementHTML, buildAgreementDataFromLease, buildSigHTML, formatDateDMY, AVAILABLE_LANGUAGES } from "../../utils/agreement";
import { getLeaseStatus, getDaysRemaining, isExpiringSoon, leaseStatusColor, leaseStatusLabel } from "../../utils/leaseHelpers";

// Wrapper around Fld for clause fields: always shows an English input,
// and (behind a toggle) a per-language translation input. Value is stored
// as either a plain string (legacy / no translation) or an object
// { en: "...", [langCode]: "..." } when the landlord provides a
// translation. resolveClause() in clauseLibrary.js reads both shapes.
function ClauseFld({ h, l, v, set }) {
  const valObj = (typeof v === 'object' && v !== null) ? v : null;
  const en = valObj ? (valObj.en || '') : (v || '');
  const existingLang = valObj ? Object.keys(valObj).find(k => k !== 'en') : null;
  const existingTrans = existingLang ? (valObj[existingLang] || '') : '';

  const [showTrans, setShowTrans] = useState(!!existingLang);
  const [selLang, setSelLang] = useState(existingLang || 'hi');

  const writeEn = (newEn) => {
    // If a translation slot is open with content, keep as object.
    if (showTrans && existingTrans) {
      set({ en: newEn, [selLang]: existingTrans });
    } else {
      set(newEn || '');
    }
  };

  const writeTrans = (newTrans) => {
    if (newTrans) {
      set({ en, [selLang]: newTrans });
    } else if (en) {
      // Translation cleared — collapse back to plain string.
      set(en);
    } else {
      set('');
    }
  };

  const changeLang = (newLang) => {
    // Move the existing translation text under the new language key.
    if (valObj && existingLang && existingTrans) {
      const obj = { en, [newLang]: existingTrans };
      set(obj);
    }
    setSelLang(newLang);
  };

  const removeTrans = () => {
    setShowTrans(false);
    set(en || '');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <Fld h={h} l={l + " (English)"} v={en} set={writeEn} multiline />
      {showTrans ? (
        <div style={{
          marginTop: -4, marginBottom: 8, marginLeft: 12,
          paddingLeft: 12, paddingTop: 8,
          borderLeft: `2px solid ${h.ac}40`,
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: h.sc, fontWeight: 600 }}>Translate to:</span>
            <select
              value={selLang}
              onChange={(e) => changeLang(e.target.value)}
              style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx }}
            >
              {AVAILABLE_LANGUAGES.filter(o => o.code !== 'en').map(o => (
                <option key={o.code} value={o.code}>{o.label} / {o.nativeLabel}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={removeTrans}
              style={{ background: "none", border: "none", color: h.rd, fontSize: 11, cursor: "pointer", padding: 0 }}
            >
              × Remove translation
            </button>
          </div>
          <Fld h={h} l={null} v={existingTrans} set={writeTrans} multiline ph="Paste or type your translation here…" />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowTrans(true)}
          style={{ background: "none", border: "none", color: h.ac, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "0 0 8px 0" }}
        >
          + Add translation
        </button>
      )}
    </div>
  );
}

function AgreementPage() {
  const { t, h, leases, setLeases, props, tens, setTens, go, flash, showConfirm, settings, setSettings } = useContext(Ctx);
  const parseLLInfo = () => { try { return JSON.parse(settings?.landlord_info || '{}'); } catch { return {}; } };
  const savedLLInfo = parseLLInfo();
  const [view, setView] = useState("list"); // list | detail | edit
  const [selId, setSelId] = useState(null);
  const [tab, setTab] = useState("all");
  const [showLLInfoEdit, setShowLLInfoEdit] = useState(false);
  const [llInfo, setLLInfo] = useState(savedLLInfo);
  const setLI = (k, v) => setLLInfo(p => ({ ...p, [k]: v }));
  const [showLLSigPad, setShowLLSigPad] = useState(false);
  const [showTenSigPad, setShowTenSigPad] = useState(false);
  const [showWitSigPad, setShowWitSigPad] = useState(null); // null | 'w1' | 'w2' | 'tw1' | 'tw2'

  // Language selection for PDF generation. Defaults to English + Tamil
  // to match the behaviour before language selection was added. Persisted
  // to localStorage so a landlord who picks EN+HI once keeps that choice
  // across sessions.
  const [pdfLangs, setPdfLangs] = useState(() => {
    try {
      const raw = localStorage.getItem('pdfLangs');
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch { /* ignore */ }
    return ['en', 'ta'];
  });
  const toggleLang = (code) => {
    setPdfLangs(prev => {
      const next = prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code];
      // Never allow zero languages — fall back to English to avoid a
      // silently empty PDF.
      const final = next.length > 0 ? next : ['en'];
      try { localStorage.setItem('pdfLangs', JSON.stringify(final)); } catch { /* ignore */ }
      return final;
    });
  };

  // ── Selected lease + related data ──
  const sel = leases.find(l => l.id === selId);
  const selTen = sel ? tens.find(x => x.id === sel.tenant_id) : null;
  const selProp = sel ? props.find(x => x.id === sel.property_id) : null;

  // ── Edit state (only when view === 'edit') ──
  const [editLease, setEditLease] = useState({});
  const [editTerms, setEditTerms] = useState({});
  const setEL = (k, v) => setEditLease(p => ({ ...p, [k]: v }));
  const setET = (k, v) => setEditTerms(p => ({ ...p, [k]: v }));

  const parseTerms = (l) => { try { return typeof l?.terms === 'string' ? JSON.parse(l.terms) : (l?.terms || {}); } catch { return {}; } };

  const openDetail = (l) => { setSelId(l.id); setView("detail"); };
  const openEdit = () => {
    if (!sel) return;
    setEditLease({ start_date: sel.start_date || '', end_date: sel.end_date || '', rent: sel.rent || 0, deposit: sel.deposit || 0, notice_period_days: sel.notice_period_days || 30, lock_in_months: sel.lock_in_months || '', escalation_pct: sel.escalation_pct || '', escalation_interval_months: sel.escalation_interval_months || 12, lease_type: sel.lease_type || 'residential', status: sel.status || 'draft' });
    // Auto-populate landlord info from Settings if not already in terms
    const existingTerms = parseTerms(sel);
    const mergedTerms = { ...defaultTerms, ...existingTerms };
    if (savedLLInfo.name && !existingTerms.landlord_name) {
      mergedTerms.landlord_name = savedLLInfo.name;
      mergedTerms.ll_parent = savedLLInfo.parent || '';
      mergedTerms.ll_relation = savedLLInfo.relation || 'son of';
      mergedTerms.ll_aadhaar = savedLLInfo.aadhaar || '';
      mergedTerms.landlord_address = savedLLInfo.address || '';
      mergedTerms.district = savedLLInfo.district || '';
      mergedTerms.taluk = savedLLInfo.taluk || '';
      mergedTerms.state = savedLLInfo.state || 'Tamil Nadu';
    }
    // Auto-populate tenant-side fields from the tenant's own profile
    // (set by the tenant on their Profile page). Only fills slots that
    // are empty in the lease's cached terms — so if the landlord has
    // already typed something manually, we respect that. This matters
    // for leases that were drafted BEFORE the tenant filled their info:
    // the cached terms have empty strings; we refresh them here.
    if (selTen) {
      const landlordHasEditedTenantInfo = !!(existingTerms.ten_parent);
      if (!existingTerms.ten_parent && selTen.parent_name) {
        mergedTerms.ten_parent = selTen.parent_name;
      }
      // Relation: if the lease was auto-created before the tenant set
      // theirs, the cached value is the literal default 'son of'. Treat
      // that as "not landlord-edited" unless ten_parent is also filled.
      if (!landlordHasEditedTenantInfo && selTen.relation) {
        mergedTerms.ten_relation = selTen.relation;
      }
      if (!existingTerms.ten_aadhaar && selTen.id_number) {
        const last4 = String(selTen.id_number).replace(/\D/g, '').slice(-4);
        if (last4) mergedTerms.ten_aadhaar = `XXXX XXXX ${last4}`;
      }
      if (!existingTerms.ten_address && selTen.address) {
        mergedTerms.ten_address = selTen.address;
      }
    }
    setEditTerms(mergedTerms);
    setView("edit");
  };
  const saveEdit = async () => {
    // Always inject latest landlord info from Settings
    const finalTerms = { ...editTerms };
    if (savedLLInfo.name) {
      finalTerms.landlord_name = savedLLInfo.name;
      finalTerms.ll_parent = savedLLInfo.parent || finalTerms.ll_parent;
      finalTerms.ll_relation = savedLLInfo.relation || finalTerms.ll_relation;
      finalTerms.ll_aadhaar = savedLLInfo.aadhaar || finalTerms.ll_aadhaar;
      finalTerms.landlord_address = savedLLInfo.address || finalTerms.landlord_address;
      finalTerms.district = savedLLInfo.district || finalTerms.district;
      finalTerms.taluk = savedLLInfo.taluk || finalTerms.taluk;
      finalTerms.state = savedLLInfo.state || finalTerms.state;
    }
    const updated = { ...sel, ...editLease, terms: JSON.stringify(finalTerms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    await api.saveLease(updated);
    flash("Agreement saved \u2705");
    setView("detail");
  };

  // ── Filter leases ──
  const filtered = useMemo(() => {
    let f = leases || [];
    if (tab === "active") f = f.filter(l => l.status === "active");
    else if (tab === "draft") f = f.filter(l => l.status === "draft");
    else if (tab === "expiring") f = f.filter(l => isExpiringSoon(l));
    else if (tab === "expired") f = f.filter(l => l.status === "expired" || l.status === "terminated");
    return f;
  }, [leases, tab]);

  const stats = useMemo(() => ({
    active: (leases || []).filter(l => l.status === "active").length,
    expiring: (leases || []).filter(l => isExpiringSoon(l)).length,
    expired: (leases || []).filter(l => l.status === "expired" || l.status === "terminated").length,
    draft: (leases || []).filter(l => l.status === "draft").length,
  }), [leases]);

  // ── PDF Print ──
  const printAgreement = (lease) => {
    const ten = tens.find(x => x.id === lease.tenant_id);
    const prop = props.find(x => x.id === lease.property_id);
    const data = buildAgreementDataFromLease(lease, ten, prop);
    if (!data) { flash("Missing data for PDF", "error"); return; }
    const html = generateAgreementHTML(data, pdfLangs);

    // Use a Blob URL instead of document.write. Reason: when we pass
    // `noopener` to window.open, modern browsers return null as the
    // window handle (for tabnabbing protection), so the old
    // `w.document.write(html)` path silently opened a blank tab.
    //
    // Blob URL lets the browser load the generated HTML as an ordinary
    // page — onload fires correctly (window.print() works), and
    // `noopener` keeps the tabnabbing protection because the new page
    // has no opener reference back.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener');
    if (!w && !/safari/i.test(navigator.userAgent)) {
      // Popup blocked. Safari returns null for noopener even on success,
      // so we only show the error on non-Safari browsers.
      flash("Allow popups to view agreement", "error");
    }
    // Free the blob memory after the new tab has had time to load it.
    // 60s is generous — the browser caches the resource by then.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // ── Landlord sign for selected lease ──
  const handleLLSign = (sigData) => {
    if (!sel) return;
    const terms = { ...parseTerms(sel), landlord_signature: sigData };
    const updated = { ...sel, terms: JSON.stringify(terms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    api.saveLease(updated);
    setShowLLSigPad(false);
    flash("Landlord signature saved \u2705");
  };

  // ── Witness sign ──
  const handleWitSign = (witKey, sigData) => {
    if (!sel) return;
    const sigVal = `sig|${new Date().toLocaleDateString("en-IN")}|${sigData}`;
    const terms = { ...parseTerms(sel), [witKey]: sigVal };
    const updated = { ...sel, terms: JSON.stringify(terms) };
    setLeases(leases.map(l => l.id === sel.id ? updated : l));
    api.saveLease(updated);
    setShowWitSigPad(null);
    flash("Witness signature saved \u2705");
  };

  // ── Terminate ──
  const handleTerminate = () => {
    if (!sel) return;
    showConfirm("Terminate this agreement?", async () => {
      const updated = { ...sel, status: "terminated" };
      setLeases(leases.map(l => l.id === sel.id ? updated : l));
      await api.saveLease(updated);
      flash("Agreement terminated");
      setView("list");
    });
  };

  // ── Delete ──
  const handleDelete = () => {
    if (!sel) return;
    showConfirm("Delete this agreement permanently?", async () => {
      setLeases(leases.filter(l => l.id !== sel.id));
      await api.deleteLease(sel.id);
      flash("Agreement deleted");
      setView("list");
    });
  };

  // ═══════════════════════════════════════════════════════════════
  // VIEW A: LIST
  // ═══════════════════════════════════════════════════════════════
  if (view === "list") {
    const tabs = [["all", "All"], ["active", `Active (${stats.active})`], ["expiring", `Expiring (${stats.expiring})`], ["draft", `Draft (${stats.draft})`], ["expired", "Expired"]];
    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>{t.agreements}</h2>

      {/* Landlord Agreement Info */}
      <div style={{ ...crd(h), marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showLLInfoEdit ? 10 : 0 }}>
          <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{"\uD83C\uDFE0"} Landlord Info</div>
          <button onClick={() => { setLLInfo(parseLLInfo()); setShowLLInfoEdit(!showLLInfoEdit); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
            {showLLInfoEdit ? "Close" : savedLLInfo.name ? "\u270F\uFE0F Edit" : "+ Setup"}
          </button>
        </div>
        {!showLLInfoEdit && savedLLInfo.name && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{savedLLInfo.name} {savedLLInfo.parent ? `(${savedLLInfo.relation || "S/o"} ${savedLLInfo.parent})` : ""} {"\u2022"} {savedLLInfo.district || ""}</div>}
        {!showLLInfoEdit && !savedLLInfo.name && <div style={{ color: h.wn, fontSize: 11, marginTop: 4 }}>{"\u26A0\uFE0F"} Not configured — tap Setup to add your details</div>}
        {showLLInfoEdit && <div>
          <input value={llInfo.name || ''} onChange={e => setLI("name", e.target.value)} placeholder="Full Name *" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <input value={llInfo.parent || ''} onChange={e => setLI("parent", e.target.value)} placeholder="Father/Husband" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
            <select value={llInfo.relation || 'son of'} onChange={e => setLI("relation", e.target.value)} style={{ width: 90, padding: "10px 8px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, marginBottom: 6 }}><option>son of</option><option>wife of</option><option>daughter of</option></select>
          </div>
          <input value={llInfo.aadhaar || ''} onChange={e => setLI("aadhaar", e.target.value)} placeholder="Aadhaar No" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <input value={llInfo.address || ''} onChange={e => setLI("address", e.target.value)} placeholder="Address" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <input value={llInfo.district || ''} onChange={e => setLI("district", e.target.value)} placeholder="District" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
            <input value={llInfo.taluk || ''} onChange={e => setLI("taluk", e.target.value)} placeholder="Taluk" style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 12, boxSizing: "border-box", marginBottom: 6 }} />
          </div>
          <input value={llInfo.state || 'Tamil Nadu'} onChange={e => setLI("state", e.target.value)} placeholder="State" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 8 }} />
          <button onClick={async () => {
            await api.saveSetting("landlord_info", JSON.stringify(llInfo));
            setSettings(s => ({ ...s, landlord_info: JSON.stringify(llInfo) }));
            setShowLLInfoEdit(false);
            flash("Landlord info saved \u2705");
          }} style={{ width: "100%", padding: 10, borderRadius: 10, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{"\u2705"} Save</button>
        </div>}
      </div>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["active", h.gn, "\u2713", "Active"], ["expiring", "#f59e0b", "!", "Expiring"], ["expired", h.rd, "\u2715", "Expired"]].map(([k, c, ic, lb]) => (
          <div key={k} style={{ flex: 1, padding: "10px 14px", borderRadius: 12, background: `${c}08`, border: `1px solid ${c}20` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ color: c, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{ic} {lb}</span>
            </div>
            <div style={{ color: c, fontSize: 20, fontWeight: 700 }}>{stats[k]}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {tabs.map(([k, lb]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${tab === k ? h.ac : h.bd}`, background: tab === k ? `${h.ac}15` : "transparent", color: tab === k ? h.ac : h.sc, cursor: "pointer", fontSize: 12, fontWeight: tab === k ? 600 : 400, whiteSpace: "nowrap" }}>{lb}</button>
        ))}
      </div>

      {/* Agreement cards */}
      {filtered.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 24 }}><Ic n="file" s={32} c={h.sc} /><div style={{ color: h.sc, fontSize: 14, marginTop: 10 }}>No agreements found</div></div>}
      {filtered.map(l => {
        const ten = tens.find(x => x.id === l.tenant_id);
        const prop = props.find(x => x.id === l.property_id);
        const st = getLeaseStatus(l);
        const days = getDaysRemaining(l);
        const stC = leaseStatusColor(st, h);
        return <div key={l.id} onClick={() => openDetail(l)} style={{ ...crd(h), cursor: "pointer", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{ten?.name || "\u2014"}</div>
              <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{prop?.addr || "\u2014"}</div>
            </div>
            <span style={bdg(`${stC}20`, stC)}>{leaseStatusLabel(st, t)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, color: h.sc, fontSize: 11 }}>
            <span>{l.start_date} \u2192 {l.end_date || "\u2014"}</span>
            <span style={{ fontWeight: 600, color: h.tx }}>₹{Number(l.rent || 0).toLocaleString()}/mo</span>
          </div>
          {days != null && days >= 0 && days <= 30 && <div style={{ color: "#f59e0b", fontSize: 10, fontWeight: 600, marginTop: 4 }}>\u26A0\uFE0F {days} days remaining</div>}
        </div>;
      })}
    </div>;
  }

  // ═══════════════════════════════════════════════════════════════
  // VIEW B: DETAIL
  // ═══════════════════════════════════════════════════════════════
  if (view === "detail" && sel) {
    const st = getLeaseStatus(sel);
    const stC = leaseStatusColor(st, h);
    const days = getDaysRemaining(sel);
    const terms = { ...defaultTerms, ...parseTerms(sel) };
    const llSig = terms.landlord_signature;
    const tenSig = selTen?.esign;

    const row = (label, val) => <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${h.bd}30` }}>
      <span style={{ color: h.sc, fontSize: 12 }}>{label}</span>
      <span style={{ color: h.tx, fontSize: 12, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{val || "\u2014"}</span>
    </div>;

    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => setView("list")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.agreements}</h2>
        <span style={bdg(`${stC}20`, stC)}>{leaseStatusLabel(st, t)}</span>
      </div>

      {/* Expiring warning */}
      {isExpiringSoon(sel) && <div style={{ background: `${h.wn}15`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>{"\u26A0\uFE0F"}</span>
        <span style={{ color: h.wn, fontSize: 12, fontWeight: 600 }}>{days} days remaining</span>
      </div>}

      {/* Tenant & Property */}
      <div style={crd(h)}>
        <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Parties</div>
        {row("Tenant", selTen?.name)}
        {row("Phone", selTen?.ph)}
        {row("Property", selProp?.addr)}
        {row("Rooms", selProp?.rooms)}
      </div>

      {/* Lease Terms */}
      <div style={crd(h)}>
        <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Lease Terms</div>
        {row("Start Date", sel.start_date)}
        {row("End Date", sel.end_date)}
        {row("Rent", `₹${Number(sel.rent || 0).toLocaleString()}/mo`)}
        {row("Deposit", `₹${Number(sel.deposit || 0).toLocaleString()}`)}
        {row("Notice Period", `${sel.notice_period_days || 30} days`)}
        {sel.lock_in_months && row("Lock-in", `${sel.lock_in_months} months`)}
        {sel.escalation_pct && row("Escalation", `${sel.escalation_pct}% every ${sel.escalation_interval_months || 12} months`)}
        {row("Type", sel.lease_type === "commercial" ? "Commercial" : "Residential")}
      </div>

      {/* Agreement Clauses — inline editable */}
      {(() => {
        const clauseFields = [
          { key: "maintenance_clause", label: "Maintenance" },
          { key: "usage_clause", label: "Usage" },
          { key: "electricity_clause", label: "Electricity / Water" },
          { key: "property_tax_clause", label: "Property Tax" },
          { key: "painting_clause", label: "Painting / Repair" },
          { key: "special_terms", label: "Special Terms" },
          { key: "fittings", label: "Fittings & Fixtures" },
        ];
        const hasAny = clauseFields.some(c => terms[c.key]);
        return <div style={crd(h)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ color: h.ac, fontWeight: 600, fontSize: 12 }}>Agreement Clauses</div>
            <button onClick={openEdit} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"\u270F\uFE0F"} Edit All</button>
          </div>
          {clauseFields.map(c => {
            // A clause may be stored as a plain string (legacy / custom English)
            // OR as an object { en, [langCode]: "..." } when the landlord
            // added a translation. Extract something React can render.
            const raw = terms[c.key];
            if (!raw) return null;
            const enText = (typeof raw === 'object') ? (raw.en || '') : raw;
            const transLangs = (typeof raw === 'object')
                ? Object.keys(raw).filter(k => k !== 'en' && raw[k])
                : [];
            return (
              <div key={c.key} style={{ marginBottom: 8 }}>
                <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                  {c.label}
                  {transLangs.length > 0 && (
                    <span style={{ color: h.gn, fontSize: 10, fontWeight: 500, marginLeft: 6 }}>
                      + {transLangs.join(', ').toUpperCase()} translation
                    </span>
                  )}
                </div>
                <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{enText}</div>
              </div>
            );
          })}
          {/* Custom clauses */}
          {(terms.custom_clauses || []).map((cc, i) => cc.title || cc.text ? <div key={`cc_${i}`} style={{ marginBottom: 8 }}>
            <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{cc.title || `Custom Clause ${i + 1}`}</div>
            <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{cc.text}</div>
          </div> : null)}
          {!hasAny && !(terms.custom_clauses || []).length && <div style={{ color: h.sc, fontSize: 11, fontStyle: "italic" }}>No clauses added yet. Tap "Edit All" to add.</div>}
        </div>;
      })()}

      {/* Witnesses with e-sign */}
      <div style={crd(h)}>
        <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Witnesses</div>
        {[
          { label: "Witness 1 (Landlord)", name: terms.witness1_name, addr: terms.witness1_addr, sigKey: "witness1_sig", sig: terms.witness1_sig, padKey: "w1" },
          { label: "Witness 2 (Landlord)", name: terms.witness2_name, addr: terms.witness2_addr, sigKey: "witness2_sig", sig: terms.witness2_sig, padKey: "w2" },
          { label: "Witness 1 (Tenant)", name: terms.ten_witness1_name, addr: terms.ten_witness1_addr, sigKey: "ten_witness1_sig", sig: terms.ten_witness1_sig, padKey: "tw1" },
          { label: "Witness 2 (Tenant)", name: terms.ten_witness2_name, addr: terms.ten_witness2_addr, sigKey: "ten_witness2_sig", sig: terms.ten_witness2_sig, padKey: "tw2" },
        ].map((w, i) => (
          <div key={i} style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: `${h.bg}`, border: `1px solid ${h.bd}40` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{w.label}</span>
              {w.name && <span style={{ color: h.sc, fontSize: 10 }}>{w.name}</span>}
            </div>
            {w.addr && <div style={{ color: h.sc, fontSize: 10, marginBottom: 6 }}>{w.addr}</div>}
            {w.sig && w.sig.startsWith("sig|") ? (
              <div>
                <div style={{ background: `${h.gn}10`, padding: 8, borderRadius: 6, border: `1px solid ${h.gn}20`, marginBottom: 4 }}>
                  <div style={{ color: h.gn, fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>{"\u2713"} Signed</div>
                  <img src={w.sig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 40 }} />
                  <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {w.sig.split("|")[1]}</div>
                </div>
                <button onClick={() => setShowWitSigPad(w.padKey)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0 }}>{"\u270F\uFE0F"} Re-Sign</button>
              </div>
            ) : (
              <button onClick={() => setShowWitSigPad(w.padKey)} style={{ ...btnSec(h), width: "100%", padding: 8, fontSize: 11 }}>{"\u270D\uFE0F"} Draw Signature</button>
            )}
            {showWitSigPad === w.padKey && <div style={{ marginTop: 8, border: `2px solid ${h.ac}40`, borderRadius: 10, padding: 10 }}>
              <SignaturePad h={h} onSave={(data) => handleWitSign(w.sigKey, data)} onCancel={() => setShowWitSigPad(null)} label={w.label} />
            </div>}
          </div>
        ))}
      </div>

      {/* Signatures */}
      <div style={crd(h)}>
        <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Signatures</div>
        {/* Landlord */}
        <div style={{ marginBottom: 10 }}>
          {llSig ? <div>
            <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
              <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Landlord Signed</div>
              {llSig.startsWith("sig|") && <img src={llSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50 }} />}
              {llSig.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {llSig.split("|")[1]}</div>}
            </div>
            <button onClick={() => setShowLLSigPad(true)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0, marginTop: 4 }}>{"\u270F\uFE0F"} Re-Sign</button>
          </div> : <button onClick={() => setShowLLSigPad(true)} style={{ ...btnP(h), width: "100%", padding: 10 }}>{"\u270D\uFE0F"} Sign as Landlord</button>}
        </div>
        {/* Tenant */}
        <div>
          {tenSig ? <div>
            <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
              <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Tenant Signed</div>
              {tenSig.startsWith("sig|") && <img src={tenSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50 }} />}
              {!tenSig.startsWith("sig|") && <img src={tenSig} alt="sig" style={{ maxHeight: 50 }} />}
              {tenSig.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {tenSig.split("|")[1]}</div>}
            </div>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 4, fontStyle: "italic" }}>Tenant can re-sign from their own login</div>
          </div> : <div style={{ background: `${h.wn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.wn}25` }}>
            <div style={{ color: h.wn, fontSize: 11, fontWeight: 600 }}>{"\u23F3"} Awaiting tenant signature</div>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 4 }}>Tenant signs from their app: My Agreement {"\u2192"} Sign & Activate</div>
          </div>}
        </div>
      </div>

      {/* Landlord sig pad modal */}
      {showLLSigPad && <div style={{ ...crd(h), border: `2px solid ${h.ac}40` }}>
        <SignaturePad h={h} onSave={(data) => handleLLSign(`sig|${new Date().toLocaleDateString("en-IN")}|${data}`)} onCancel={() => setShowLLSigPad(false)} label="Landlord Signature" />
      </div>}

      {/* PDF language picker — toggle chips. Selection is remembered
          across sessions (localStorage). Used when the PDF button below
          assembles the agreement document. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
          PDF languages:
        </div>
        {AVAILABLE_LANGUAGES.map(lang => {
          const on = pdfLangs.includes(lang.code);
          return (
            <button
              key={lang.code}
              onClick={() => toggleLang(lang.code)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${on ? h.ac : h.bd}`,
                background: on ? `${h.ac}18` : h.ib,
                color: on ? h.ac : h.tx,
                fontSize: 12,
                fontWeight: on ? 700 : 500,
                cursor: "pointer",
                transition: "0.15s",
              }}
              type="button"
              aria-pressed={on}
            >
              {on ? "\u2713 " : ""}{lang.nativeLabel}
            </button>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button onClick={openEdit} style={{ ...btnP(h), flex: 1, padding: 10 }}>{"\u270F\uFE0F"} Edit</button>
        <button onClick={() => printAgreement(sel)} style={{ ...btnSec(h), flex: 1, padding: 10 }}>{"\uD83D\uDCC4"} PDF</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {sel.status !== "terminated" && <button onClick={handleTerminate} style={{ ...btnSec(h), flex: 1, padding: 10, color: h.wn, borderColor: `${h.wn}30` }}>{"\u26D4"} Terminate</button>}
        <button onClick={handleDelete} style={{ ...btnSec(h), flex: 1, padding: 10, color: h.rd, borderColor: `${h.rd}30` }}>{"\u2715"} Delete</button>
      </div>
    </div>;
  }

  // ═══════════════════════════════════════════════════════════════
  // VIEW C: EDIT
  // ═══════════════════════════════════════════════════════════════
  if (view === "edit") {
    const sec = (title) => <div style={{ color: h.ac, fontWeight: 700, fontSize: 13, marginTop: 16, marginBottom: 8 }}>{title}</div>;
    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => setView("detail")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Edit Agreement</h2>

      {/* Lease Fields */}
      <div style={crd(h)}>
        {sec("Lease Terms")}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4 }}>Start Date</label><input type="date" value={editLease.start_date || ''} onChange={e => setEL("start_date", e.target.value)} style={inpS(h)} /></div>
          <div style={{ flex: 1 }}><label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4 }}>End Date</label><input type="date" value={editLease.end_date || ''} onChange={e => setEL("end_date", e.target.value)} style={inpS(h)} /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Rent (₹)" v={editLease.rent} set={v => setEL("rent", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Deposit (₹)" v={editLease.deposit} set={v => setEL("deposit", Number(v))} type="number" /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Notice (days)" v={editLease.notice_period_days} set={v => setEL("notice_period_days", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Lock-in (months)" v={editLease.lock_in_months} set={v => setEL("lock_in_months", Number(v))} type="number" /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Escalation %" v={editLease.escalation_pct} set={v => setEL("escalation_pct", Number(v))} type="number" /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Esc. Interval (mo)" v={editLease.escalation_interval_months} set={v => setEL("escalation_interval_months", Number(v))} type="number" /></div>
        </div>
        <label style={{ color: h.sc, fontSize: 11, display: "block", marginBottom: 4, marginTop: 4 }}>Status</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {["draft", "active"].map(s => (
            <button key={s} onClick={() => setEL("status", s)} style={{ flex: 1, padding: 8, borderRadius: 10, border: `1px solid ${editLease.status === s ? h.ac : h.bd}`, background: editLease.status === s ? `${h.ac}15` : "transparent", color: editLease.status === s ? h.ac : h.sc, cursor: "pointer", fontSize: 12, textTransform: "capitalize" }}>{s}</button>
          ))}
        </div>
      </div>

      {/* Landlord Info — auto-populated from Settings */}
      <div style={crd(h)}>
        {sec("Landlord Information")}
        {savedLLInfo.name ? <>
          <div style={{ background: `${h.gn}08`, borderRadius: 8, padding: 10, border: `1px solid ${h.gn}20`, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: h.gn, fontSize: 10, fontWeight: 700 }}>{"\u2705"} Using saved landlord info</span>
              <button onClick={() => go("settings")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{"\u2699\uFE0F"} Edit in Settings</button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: h.tx, lineHeight: 1.8 }}>
            <div><b>{savedLLInfo.name}</b> {savedLLInfo.parent ? `(${savedLLInfo.relation || "S/o"} ${savedLLInfo.parent})` : ""}</div>
            {savedLLInfo.aadhaar && <div style={{ color: h.sc }}>Aadhaar: {savedLLInfo.aadhaar}</div>}
            {savedLLInfo.address && <div style={{ color: h.sc }}>{savedLLInfo.address}</div>}
            {(savedLLInfo.district || savedLLInfo.taluk) && <div style={{ color: h.sc }}>{[savedLLInfo.taluk, savedLLInfo.district, savedLLInfo.state].filter(Boolean).join(", ")}</div>}
          </div>
        </> : <div style={{ background: `${h.wn}10`, borderRadius: 8, padding: 12, border: `1px solid ${h.wn}25` }}>
          <div style={{ color: h.wn, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{"\u26A0\uFE0F"} Landlord info not configured</div>
          <div style={{ color: h.sc, fontSize: 11, marginBottom: 8 }}>Go to Settings to add your name, aadhaar, address. It will auto-fill in all agreements.</div>
          <button onClick={() => go("settings")} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"\u2699\uFE0F"} Go to Settings</button>
        </div>}
      </div>

      {/* Tenant Info */}
      <div style={crd(h)}>
        {sec("Tenant Agreement Details")}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant's Parent" v={editTerms.ten_parent} set={v => setET("ten_parent", v)} /></div>
          <div style={{ flex: 1 }}>
            <label style={{ color: h.sc, fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5, letterSpacing: "0.3px", textTransform: "uppercase" }}>Relation</label>
            <select value={editTerms.ten_relation || 'son of'} onChange={e => setET("ten_relation", e.target.value)} style={inpS(h)}>
              <option>son of</option><option>wife of</option><option>daughter of</option><option>husband of</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant Aadhaar" v={editTerms.ten_aadhaar} set={v => setET("ten_aadhaar", v)} /></div>
          <div style={{ flex: 1 }}><Fld h={h} l="Tenant Address" v={editTerms.ten_address} set={v => setET("ten_address", v)} /></div>
        </div>
      </div>

      {/* Clauses */}
      <div style={crd(h)}>
        {sec("Agreement Clauses")}
        <ClauseFld h={h} l="Maintenance"     v={editTerms.maintenance_clause}  set={v => setET("maintenance_clause", v)} />
        <ClauseFld h={h} l="Usage"           v={editTerms.usage_clause}        set={v => setET("usage_clause", v)} />
        <ClauseFld h={h} l="Electricity/Water" v={editTerms.electricity_clause} set={v => setET("electricity_clause", v)} />
        <ClauseFld h={h} l="Property Tax"    v={editTerms.property_tax_clause} set={v => setET("property_tax_clause", v)} />
        <ClauseFld h={h} l="Painting/Repair" v={editTerms.painting_clause}     set={v => setET("painting_clause", v)} />
        <ClauseFld h={h} l="Special Terms"   v={editTerms.special_terms}       set={v => setET("special_terms", v)} />
        <Fld h={h} l="Fittings & Fixtures" v={editTerms.fittings} set={v => setET("fittings", v)} multiline />

        {/* Custom Clauses */}
        {(editTerms.custom_clauses || []).map((cc, i) => (
          <div key={i} style={{ position: "relative", marginTop: 8, padding: 10, borderRadius: 10, border: `1px solid ${h.bd}40`, background: `${h.bg}` }}>
            <button onClick={() => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated.splice(i, 1);
              setET("custom_clauses", updated);
            }} style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: h.rd, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>{"\u2715"}</button>
            <Fld h={h} l={`Clause ${i + 1} Title`} v={cc.title} set={v => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated[i] = { ...updated[i], title: v };
              setET("custom_clauses", updated);
            }} ph="e.g. Pet Policy" />
            <Fld h={h} l="Description" v={cc.text} set={v => {
              const updated = [...(editTerms.custom_clauses || [])];
              updated[i] = { ...updated[i], text: v };
              setET("custom_clauses", updated);
            }} multiline ph="Enter clause details..." />
          </div>
        ))}

        <button onClick={() => {
          const updated = [...(editTerms.custom_clauses || []), { title: "", text: "" }];
          setET("custom_clauses", updated);
        }} style={{ ...btnSec(h), width: "100%", marginTop: 12, padding: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>+</span> Add Clause
        </button>
      </div>

      {/* Save/Cancel */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button onClick={saveEdit} style={{ ...btnP(h), flex: 2, padding: 12 }}>{"\u2705"} Save Agreement</button>
        <button onClick={() => setView("detail")} style={{ ...btnSec(h), flex: 1, padding: 12 }}>Cancel</button>
      </div>
    </div>;
  }

  // Fallback
  return <div style={{ paddingTop: 16 }}><button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13 }}>&larr; {t.back}</button></div>;
}

export default AgreementPage;
